import { useState, useEffect } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";

import "../css/pagination.css";
import { ReactComponent as LeftIcon } from "../assets/icons/left_arrow.svg";
import { ReactComponent as RightIcon } from "../assets/icons/rightArrow.svg";
import CustomDropdownIndicatorGrey from "../elements/CustomDropdownIndicatorGrey";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#F8F9F9",
    borderColor: "#7ECEA7",
    color: "#205A46",
    boxShadow: state.isFocused ? "0 0 0 1pxrgb(137, 138, 137)" : "none",
    borderWidth: "1px",
    borderRadius: "10px",
    "&:hover": {
      borderColor: "#7ECEA7",
    },
    minHeight: "35px",
    height: "auto",
    width: "60px",
    padding: 0,
    outline: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 8px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#4d6d64",
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#b3c0bd",
    fontSize: "13px",
    lineHeight: "1.4",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#4d6d64",
    fontSize: "13px",
    lineHeight: "1.4",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#ffffff",
    border: "0.5px solid #c2e6d1",
    borderRadius: "6px",
    boxShadow: "0px 10px 30px 0px #0A322424;",
    marginTop: "6px",
    zIndex: 10,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "4px",
    borderRadius: "6px",
    maxHeight: "none", 
    overflowY: "visible", 
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#c2e6d1"
      : state.isFocused
      ? "#f4faf5"
      : "transparent",
    color: "#4f665f",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "13px",
    borderRadius: "6px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding:"5px"
  }),
};

export default function PaginationFooter({
  paginationParams,
  paginationHandler,
}) {
  const animatedComponents = makeAnimated();

  const [itemsPerPage, setItemsPerPage] = useState(paginationParams.pageSize);
  const [currentPage, setCurrentPage] = useState(paginationParams.currentPage);
  const totalPages = Array.from(
    { length: paginationParams.totalPages },
    (_, i) => i + 1
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    paginationHandler({ page: page, limit: itemsPerPage });
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      paginationHandler({ page: currentPage - 1, limit: itemsPerPage });
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages.length) {
      paginationHandler({ page: currentPage + 1, limit: itemsPerPage });
      setCurrentPage(currentPage + 1);
    }
  };
  function getVisiblePages(totalPages, currentPage) {
    const delta = 2; 
    const pages = [];
    const range = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      range.unshift("...");
    }

    if (currentPage + delta < totalPages - 1) {
      range.push("...");
    }

    range.unshift(1);
    if (totalPages > 1) range.push(totalPages);

    return range;
  }
  const options = [
    { value: 1, label: "1" },
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 30, label: "30" },
    { value: 50, label: "50" },
  ];

  return (
    <div className="pagination-footer">
      {/* Left: Items per page */}
      <div
        className="pagination-left"
        style={{ width: "auto", display: "flex", "align-items": "baseline" }}
      >
        <label>Rows Per Page </label>

        <Select
          value={{ value: itemsPerPage, label: itemsPerPage }}
          onChange={(e) => {
            setItemsPerPage(e.value);
            setCurrentPage(1);
            paginationHandler({ page: 1, limit: e.value });
          }}
          menuPlacement="top"
          options={options}
          className="mt-0"
          classNamePrefix="select"
          components={{
            ...animatedComponents,
            IndicatorSeparator: () => null,
            DropdownIndicator: CustomDropdownIndicatorGrey,
          }}
          styles={{
            ...customStyles,
            input: (provided) => ({
              ...provided,
              caretColor: "transparent",
              color: "#4d6d64",
              margin: 0,
              padding: 0,
              border: "none", 
              boxShadow: "none", 
              outline: "none", 
              backgroundColor: "transparent", 
              fontSize: "13px",
              lineHeight: "1.4",
            }),
          }}
        />
      </div>

      {/* Center: Page numbers */}
      <div className="pagination-center">
        {getVisiblePages(paginationParams.totalPages, currentPage).map(
          (page, index) => (
            <button
              key={index}
              disabled={page === "..."}
              className={`page-btn ${currentPage === page ? "active" : ""}`}
              onClick={() => typeof page === "number" && handlePageClick(page)}
            >
              {page}
            </button>
          )
        )}
      </div>

      {/* Right: Prev / Next */}
      <div className="pagination-right">
        <button onClick={handlePrev} disabled={currentPage === 1}>
          <LeftIcon className="vaMiddle" width={10} height={10} />
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages.length}
        >
          <RightIcon className="vaMiddle" width={10} height={10} />
        </button>
      </div>
    </div>
  );
}
